/**
 * Gestão de Cursos — módulo P0 para Diretoria/Supervisão (Iter 33).
 *
 * Lista cursos com contagem de participantes. Clique abre detalhe do curso
 * onde é possível: editar campos obrigatórios (professor, datas, carga, PJ,
 * local, diretor de núcleo), gerenciar participantes (auto-sync de inscrições
 * + adicionar manual + remover cancelamentos) e baixar todos os PDFs em .zip.
 */
import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Users, Calendar, MapPin, GraduationCap, Package, Download,
  Trash2, Plus, ChevronLeft, FileText, Building2, Clock, Copy,
} from "lucide-react";
import { UfCursoSelect } from "@/components/UfCursoSelect";
import { CursosDuplicadosModal } from "@/components/CursosDuplicadosModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UFS_BR = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];
const MESES_BR = [
  { v: "01", l: "Jan" }, { v: "02", l: "Fev" }, { v: "03", l: "Mar" }, { v: "04", l: "Abr" },
  { v: "05", l: "Mai" }, { v: "06", l: "Jun" }, { v: "07", l: "Jul" }, { v: "08", l: "Ago" },
  { v: "09", l: "Set" }, { v: "10", l: "Out" }, { v: "11", l: "Nov" }, { v: "12", l: "Dez" },
];

// Extrai apenas a cidade do campo `local_realizacao` (que é texto livre).
// Ex: "Hotel Nacional, Brasília/DF" → "Brasília"
// Ex: "Brasília/DF" → "Brasília"
// Ex: "Brasília" → "Brasília"
function apenasCidade(txt) {
  if (!txt) return "";
  const partes = String(txt).split(",").map((s) => s.trim()).filter(Boolean);
  const ultima = partes[partes.length - 1] || "";
  return ultima.split("/")[0].trim();
}

export default function GestaoCursos() {
  const [cursos, setCursos] = useState([]);
  const [selecionado, setSelecionado] = useState(null); // curso detail
  const [loading, setLoading] = useState(false);
  const [novoOpen, setNovoOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [nucleos, setNucleos] = useState([]);
  // Iter 58.5: filtros UF + Mês
  const now = new Date();
  const [filtroUf, setFiltroUf] = useState("");
  const [filtroAno, setFiltroAno] = useState(String(now.getFullYear()));
  const [filtroMes, setFiltroMes] = useState(String(now.getMonth() + 1).padStart(2, "0"));

  const load = () => {
    setLoading(true);
    api.get("/gestao-cursos").then((r) => setCursos(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/nucleos").then((r) => setNucleos(r.data || [])).catch(() => {});
  }, []);

  const anosDisponiveis = useMemo(() => {
    const set = new Set();
    (cursos || []).forEach((c) => c.mes_ano && set.add((c.mes_ano || "").split("-")[0]));
    const arr = [...set].sort().reverse();
    return arr.length ? arr : [String(now.getFullYear())];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursos]);

  const cursosFiltrados = useMemo(() => {
    return (cursos || []).filter((c) => {
      if (filtroUf && (c.uf || "").toUpperCase() !== filtroUf) return false;
      if (filtroAno && !((c.mes_ano || "").startsWith(filtroAno))) return false;
      if (filtroMes) {
        const parts = (c.mes_ano || "").split("-");
        if (parts[1] !== filtroMes) return false;
      }
      return true;
    }).sort((a, b) => (b.mes_ano || "").localeCompare(a.mes_ano || ""));
  }, [cursos, filtroUf, filtroAno, filtroMes]);

  const limparFiltros = () => { setFiltroUf(""); setFiltroAno(""); setFiltroMes(""); };
  const filtrosAtivos = filtroUf || filtroAno || filtroMes;

  if (selecionado) {
    return <DetalheCurso cursoId={selecionado} onBack={() => { setSelecionado(null); load(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="gestao-cursos-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-2">
            <GraduationCap className="w-8 h-8 text-[#1d7495]" />
            Gestão de Cursos
          </h1>
          <p className="text-slate-500 text-sm">
            Cadastre cursos, gerencie participantes e emita todos os documentos oficiais (Relação, Listas de Presença por turno, Etiquetas Credenciais).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setDupOpen(true)} data-testid="btn-cursos-duplicados">
            <Copy className="w-4 h-4 mr-1" /> Possíveis duplicados
          </Button>
          <Button onClick={() => setNovoOpen(true)} className="bg-[#1d7495] hover:bg-[#0f5673]" data-testid="btn-novo-curso">
            <Plus className="w-4 h-4 mr-1" /> Novo Curso
          </Button>
        </div>
      </div>
      <CursosDuplicadosModal open={dupOpen} onClose={() => setDupOpen(false)} />

      {/* Iter 58.5: Filtros UF + Ano + Mês */}
      <Card className="p-4 rounded-2xl">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Estado</Label>
            <Select value={filtroUf} onValueChange={(v) => setFiltroUf(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-32 mt-1" data-testid="filtro-uf"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__all__">Todos</SelectItem>
                {UFS_BR.map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Ano</Label>
            <Select value={filtroAno} onValueChange={(v) => setFiltroAno(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-28 mt-1" data-testid="filtro-ano"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                {anosDisponiveis.map((a) => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Mês</Label>
            <Select value={filtroMes} onValueChange={(v) => setFiltroMes(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-28 mt-1" data-testid="filtro-mes"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                {MESES_BR.map((m) => (<SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          {filtrosAtivos && (
            <Button variant="ghost" size="sm" onClick={limparFiltros} className="text-slate-500 hover:text-slate-800" data-testid="filtros-limpar">
              Limpar filtros
            </Button>
          )}
          <div className="ml-auto text-xs text-slate-500 font-bold">
            {cursosFiltrados.length} de {cursos.length} curso{cursos.length === 1 ? "" : "s"}
          </div>
        </div>
      </Card>

      {loading && <div className="text-slate-400 text-sm">Carregando…</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cursosFiltrados.map((c) => (
          <Card
            key={c.id}
            className="p-4 rounded-2xl hover:shadow-lg cursor-pointer transition border-2 hover:border-[#1d7495]"
            onClick={() => setSelecionado(c.id)}
            data-testid={`curso-card-${c.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-bold text-slate-900 text-base leading-tight">{c.nome}</h3>
              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
                c.status === "ativo" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"
              }`}>
                {c.status}
              </span>
            </div>
            <div className="mt-2 space-y-1 text-xs text-slate-600">
              {c.data_inicio && <div className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {c.data_inicio}{c.data_fim && ` → ${c.data_fim}`}</div>}
              {c.local_realizacao && <div className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {apenasCidade(c.local_realizacao)}{c.uf ? `/${c.uf}` : ""}</div>}
              {c.professor && <div className="flex items-center gap-1"><GraduationCap className="w-3 h-3" /> {c.professor}</div>}
              {c.carga_horaria_total && <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> {c.carga_horaria_total}</div>}
              <div className="flex items-center gap-1 mt-2 text-[#1d7495] font-bold">
                <Users className="w-4 h-4" /> {c.participantes_qtd || 0} participante{c.participantes_qtd === 1 ? "" : "s"}
              </div>
            </div>
          </Card>
        ))}
        {cursosFiltrados.length === 0 && !loading && (
          <div className="col-span-full text-center text-slate-400 py-12">
            {filtrosAtivos ? "Nenhum curso bate com esses filtros." : "Nenhum curso cadastrado ainda."}
          </div>
        )}
      </div>

      <NovoCursoModal
        open={novoOpen}
        onClose={() => setNovoOpen(false)}
        nucleos={nucleos}
        onSaved={() => { setNovoOpen(false); load(); }}
      />
    </div>
  );
}


// =============================================================================
// Detalhe do curso
// =============================================================================
function DetalheCurso({ cursoId, onBack }) {
  const [detail, setDetail] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [emissores, setEmissores] = useState([]);
  const [users, setUsers] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const load = () => {
    api.get(`/gestao-cursos/${cursoId}`).then((r) => setDetail(r.data));
  };
  useEffect(() => {
    load();
    api.get("/emissores").then((r) => setEmissores(r.data || []));
    api.get("/users").then((r) => setUsers((r.data || []).filter((u) => ["diretoria", "supervisao"].includes(u.role))));
  }, [cursoId]);

  if (!detail) return <div className="text-slate-400 text-sm p-8">Carregando…</div>;

  const c = detail.curso;
  const emissor = emissores.find((e) => e.id === c.emissor_id);
  const diretor = users.find((u) => u.id === c.diretor_nucleo_id);

  const baixarZip = async () => {
    setDownloading(true);
    try {
      // Iter 84 · parâmetro "_" força documento novo (nunca uma cópia guardada)
      const resp = await api.get(`/gestao-cursos/${cursoId}/documentos.zip`, { responseType: "blob", params: { _: Date.now() } });
      const nome = (resp.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1];
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = nome || `documentos-curso-${cursoId.slice(0, 8)}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("📦 ZIP com todos os documentos baixado!");
    } catch (e) {
      toast.error("Erro ao baixar ZIP: " + (e?.response?.data?.detail || e.message));
    } finally {
      setDownloading(false);
    }
  };

  const removerParticipante = async (pid) => {
    if (!window.confirm("Remover este participante do curso?")) return;
    try {
      await api.delete(`/gestao-cursos/${cursoId}/participantes/${pid}`);
      toast.success("Participante removido");
      load();
    } catch (e) {
      toast.error("Erro");
    }
  };

  const camposFaltando = !c.professor || !c.data_inicio || !c.data_fim
    || !c.carga_horaria_total || !c.local_realizacao || !c.diretor_nucleo_id || !c.emissor_id;

  return (
    <div className="space-y-4" data-testid="detalhe-curso-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="btn-voltar">
          <ChevronLeft className="w-4 h-4 mr-1" /> Voltar
        </Button>
        <h1 className="font-display text-2xl font-bold text-slate-900 flex-1">{c.nome}</h1>
        <Button onClick={() => setEditOpen(true)} variant="outline" data-testid="btn-editar-curso">
          ⚙️ Editar dados
        </Button>
        <Button
          onClick={async () => {
            if (!window.confirm(`Excluir permanentemente o curso "${c.nome}"?\n\nAtenção: não é possível excluir cursos com inscrições ativas.`)) return;
            try {
              await api.delete(`/cursos/${c.id}`);
              toast.success("Curso excluído");
              onBack();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Erro ao excluir");
            }
          }}
          variant="outline"
          className="text-red-600 border-red-300 hover:bg-red-50"
          data-testid="btn-excluir-curso"
        >
          <Trash2 className="w-4 h-4 mr-1" /> Excluir
        </Button>
      </div>

      {camposFaltando && (
        <Card className="p-4 rounded-xl bg-amber-50 border-amber-300 border-2">
          <div className="text-amber-900 text-sm font-semibold flex items-center gap-2">
            ⚠️ Complete os dados obrigatórios antes de emitir documentos
          </div>
          <div className="text-xs text-amber-700 mt-1">
            Faltando: {[
              !c.professor && "Professor",
              !c.data_inicio && "Data de início",
              !c.data_fim && "Data de término",
              !c.carga_horaria_total && "Carga horária",
              !c.local_realizacao && "Local",
              !c.diretor_nucleo_id && "Diretor de Núcleo",
              !c.emissor_id && "PJ Emissora",
            ].filter(Boolean).join(" · ")}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-5 rounded-2xl space-y-2">
          <div className="text-xs uppercase font-bold text-slate-500 tracking-widest mb-2">Dados do curso</div>
          <InfoRow icon={<GraduationCap />} label="Professor" value={c.professor} />
          <InfoRow icon={<Calendar />} label="Período" value={c.data_inicio && c.data_fim ? `${c.data_inicio} até ${c.data_fim}` : c.data_inicio || null} />
          <InfoRow icon={<Clock />} label="Carga horária" value={c.carga_horaria_total} />
          <InfoRow icon={<MapPin />} label="Local" value={c.local_realizacao ? `${apenasCidade(c.local_realizacao)}${c.uf ? `/${c.uf}` : ""}` : null} />
          <InfoRow icon={<Building2 />} label="PJ Emissora" value={emissor ? `${emissor.razao_social} · ${emissor.cnpj}` : null} />
          <InfoRow icon={<Users />} label="Diretor de Núcleo" value={diretor?.nome} />
        </Card>

        <Card className="p-5 rounded-2xl">
          <div className="text-xs uppercase font-bold text-slate-500 tracking-widest mb-2">
            Turnos que serão gerados
          </div>
          {detail.turnos.length === 0 ? (
            <div className="text-slate-400 text-sm">Configure data de início e término para calcular os turnos automaticamente.</div>
          ) : (
            <div className="space-y-1.5">
              {detail.turnos.map((t, i) => (
                <div key={i} className="text-sm flex items-center gap-2">
                  <span className="text-[10px] bg-slate-100 rounded px-1.5 py-0.5 font-mono">{t.data}</span>
                  <span className={t.turno.includes("Manhã") ? "text-amber-600" : "text-blue-600"}>
                    {t.turno.includes("Manhã") ? "☀️" : "🌙"} {t.turno}
                  </span>
                  <span className="text-xs text-slate-500" data-testid={`turno-horario-${i}`}>{t.horario} · {t.carga}</span>
                </div>
              ))}
              <div className="text-[10px] text-slate-500 italic mt-2">
                Regra CEAP: 1º dia só Tarde · último dia só Manhã · dias intermediários cheios
              </div>
              <Button size="sm" variant="outline" className="mt-2" onClick={() => setEditOpen(true)} data-testid="btn-editar-horarios-presenca">
                <Clock className="w-3.5 h-3.5 mr-1" /> Editar horários e carga
              </Button>
            </div>
          )}
        </Card>
      </div>

      {/* Botão principal de emissão */}
      <Card className="p-4 rounded-2xl bg-gradient-to-r from-[#1d7495] to-[#0f5673]">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-white">
            <div className="text-lg font-bold">📥 Emitir todos os documentos</div>
            <div className="text-xs opacity-80">
              Gera Relação de Participantes · {detail.turnos.length} Listas de Presença · Etiquetas Credenciais em um único ZIP
            </div>
          </div>
          <Button
            onClick={baixarZip}
            disabled={camposFaltando || downloading || detail.participantes.length === 0}
            className="bg-white text-[#1d7495] hover:bg-slate-100 font-bold"
            data-testid="btn-baixar-zip"
          >
            <Download className="w-4 h-4 mr-1" />
            {downloading ? "Gerando…" : "Baixar todos (.zip)"}
          </Button>
        </div>
        {detail.participantes.length === 0 && (
          <div className="text-white text-xs mt-2 opacity-90">
            ⚠️ Adicione participantes antes de emitir os documentos.
          </div>
        )}
      </Card>

      {/* Participantes */}
      <Card className="p-5 rounded-2xl">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-bold text-slate-900 text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-[#1d7495]" />
              Participantes ({detail.participantes.length})
            </div>
            <div className="text-xs text-slate-500">
              Auto-sincroniza com inscrições do Funil. Adicione manuais ou remova cancelamentos.
            </div>
          </div>
          <Button onClick={() => setAddOpen(true)} data-testid="btn-add-participante">
            <Plus className="w-4 h-4 mr-1" /> Adicionar manual
          </Button>
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-500 border-b">
              <th className="pb-2 pr-2">#</th>
              <th className="pb-2 pr-2">Nome</th>
              <th className="pb-2 pr-2">Função</th>
              <th className="pb-2 pr-2">Município</th>
              <th className="pb-2 pr-2">UF</th>
              <th className="pb-2 pr-2">Origem</th>
              <th className="pb-2"></th>
            </tr>
          </thead>
          <tbody>
            {detail.participantes.map((p, i) => (
              <tr key={p.id} className="border-b border-slate-100" data-testid={`participante-row-${p.id}`}>
                <td className="py-2 pr-2 text-slate-400">{i + 1}</td>
                <td className="py-2 pr-2 font-medium">{p.nome}</td>
                <td className="py-2 pr-2 text-slate-600">{p.funcao || "—"}</td>
                <td className="py-2 pr-2 text-slate-600">{p.municipio_nome || "—"}</td>
                <td className="py-2 pr-2 text-slate-500">{p.uf || "—"}</td>
                <td className="py-2 pr-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded ${
                    p.origem === "inscricao" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"
                  }`}>
                    {p.origem === "inscricao" ? "✓ inscrição" : "+ manual"}
                  </span>
                </td>
                <td className="py-2">
                  <button
                    onClick={() => removerParticipante(p.id)}
                    className="text-red-500 hover:text-red-700"
                    data-testid={`btn-remover-${p.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {detail.participantes.length === 0 && (
              <tr><td colSpan={7} className="text-center text-slate-400 py-6">Nenhum participante ainda.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* Modais */}
      <EditarCursoModal open={editOpen} onClose={() => setEditOpen(false)} curso={c}
                        emissores={emissores} users={users} onSaved={() => { setEditOpen(false); load(); }} />
      <AddParticipanteModal open={addOpen} onClose={() => setAddOpen(false)}
                             cursoId={cursoId} onSaved={() => { setAddOpen(false); load(); }} />
    </div>
  );
}


function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="text-slate-400 mt-0.5">{icon}</span>
      <div className="flex-1">
        <div className="text-[10px] uppercase text-slate-500 font-bold tracking-widest">{label}</div>
        <div className={`text-slate-900 ${!value ? "text-slate-300 italic" : "font-medium"}`}>
          {value || "não informado"}
        </div>
      </div>
    </div>
  );
}


const PERIODOS_PRESENCA = [
  { k: "manha", label: "Manhã", ini: "08:00", fim: "12:00" },
  { k: "tarde", label: "Tarde", ini: "13:30", fim: "17:30" },
];

function HorariosPresencaFields({ form, setForm }) {
  const set = (campo, v) => setForm({ ...form, [campo]: v });
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="ed-presenca-horarios">
      <div className="text-xs uppercase font-bold text-slate-500 tracking-widest">Horários das listas de presença</div>
      {PERIODOS_PRESENCA.map((p) => (
        <div key={p.k} className="grid grid-cols-4 gap-2 items-end">
          <div className="text-sm font-semibold text-slate-700 pb-2">{p.label}</div>
          <div>
            <Label className="text-xs">Início</Label>
            <Input type="time" value={form[`presenca_${p.k}_inicio`] || p.ini}
              onChange={(e) => set(`presenca_${p.k}_inicio`, e.target.value)} data-testid={`ed-${p.k}-inicio`} />
          </div>
          <div>
            <Label className="text-xs">Término</Label>
            <Input type="time" value={form[`presenca_${p.k}_fim`] || p.fim}
              onChange={(e) => set(`presenca_${p.k}_fim`, e.target.value)} data-testid={`ed-${p.k}-fim`} />
          </div>
          <div>
            <Label className="text-xs">Carga (horas)</Label>
            <Input type="number" min="0" step="0.5" placeholder="automática"
              value={form[`presenca_${p.k}_carga`] ?? ""}
              onChange={(e) => set(`presenca_${p.k}_carga`, e.target.value === "" ? null : Number(e.target.value))}
              data-testid={`ed-${p.k}-carga`} />
          </div>
        </div>
      ))}
      <p className="text-[11px] text-slate-500">Carga em branco = calculada pelos horários. Vale para todas as listas de presença deste curso.</p>
    </div>
  );
}


function EditarCursoModal({ open, onClose, curso, emissores, users, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => { if (open) setForm({ ...curso }); }, [open, curso]);

  const salvar = async () => {
    try {
      await api.patch(`/cursos/${curso.id}`, form);
      toast.success("Dados do curso atualizados");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Editar dados do curso</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nome do curso *</Label>
            <Input value={form.nome || ""} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="ed-nome" />
          </div>
          <div>
            <Label>Professor / Instrutor *</Label>
            <Input value={form.professor || ""} onChange={(e) => setForm({ ...form, professor: e.target.value })} placeholder="Ex: Prof. Dr. Silvio Almeida" data-testid="ed-professor" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Data de início *</Label>
              <Input type="date" value={form.data_inicio || ""} onChange={(e) => setForm({ ...form, data_inicio: e.target.value })} data-testid="ed-data-inicio" />
            </div>
            <div>
              <Label>Data de término *</Label>
              <Input type="date" value={form.data_fim || ""} onChange={(e) => setForm({ ...form, data_fim: e.target.value })} data-testid="ed-data-fim" />
            </div>
          </div>
          <div>
            <Label>Carga horária total *</Label>
            <Input value={form.carga_horaria_total || ""} onChange={(e) => setForm({ ...form, carga_horaria_total: e.target.value })} placeholder="Ex: 32 horas" data-testid="ed-carga" />
          </div>
          <HorariosPresencaFields form={form} setForm={setForm} />
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>UF de realização *</Label>
              <UfCursoSelect value={form.uf} onChange={(v) => setForm({ ...form, uf: v })} testid="ed-uf" />
            </div>
            <div className="col-span-2">
              <Label>Cidade de realização {form.uf === "Online" ? "(opcional)" : "*"}</Label>
              <Input value={form.local_realizacao || ""} onChange={(e) => setForm({ ...form, local_realizacao: e.target.value })} placeholder="Ex: Hotel Nacional, Brasília/DF" data-testid="ed-local" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Valor cheio unitário *</Label>
              <Input type="number" step="0.01" value={form.valor_cheio ?? ""} onChange={(e) => setForm({ ...form, valor_cheio: Number(e.target.value) || 0 })} data-testid="ed-valor" />
            </div>
            <div>
              <Label>Meta inscritos *</Label>
              <Input type="number" value={form.meta_inscritos ?? ""} onChange={(e) => setForm({ ...form, meta_inscritos: Number(e.target.value) || 0 })} data-testid="ed-meta-i" />
            </div>
            <div>
              <Label>Meta receita *</Label>
              <Input type="number" step="0.01" value={form.meta_receita ?? ""} onChange={(e) => setForm({ ...form, meta_receita: Number(e.target.value) || 0 })} data-testid="ed-meta-r" />
            </div>
          </div>
          <div>
            <Label>PJ Emissora (fatura) *</Label>
            <Select value={form.emissor_id || ""} onValueChange={(v) => setForm({ ...form, emissor_id: v })}>
              <SelectTrigger data-testid="ed-emissor"><SelectValue placeholder="Selecionar…" /></SelectTrigger>
              <SelectContent>
                {emissores.map((e) => (<SelectItem key={e.id} value={e.id}>{e.razao_social} · {e.cnpj}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Diretor de Núcleo responsável *</Label>
            <Select value={form.diretor_nucleo_id || ""} onValueChange={(v) => setForm({ ...form, diretor_nucleo_id: v })}>
              <SelectTrigger data-testid="ed-diretor"><SelectValue placeholder="Selecionar…" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.nome} · {u.role}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={salvar} data-testid="btn-salvar-curso">Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AddParticipanteModal({ open, onClose, cursoId, onSaved }) {
  const [form, setForm] = useState({ nome: "", funcao: "Outro", municipio_nome: "", uf: "" });
  useEffect(() => { if (open) setForm({ nome: "", funcao: "Outro", municipio_nome: "", uf: "" }); }, [open]);

  const salvar = async () => {
    if (!form.nome.trim()) { toast.error("Nome é obrigatório"); return; }
    try {
      await api.post(`/gestao-cursos/${cursoId}/participantes`, form);
      toast.success("Participante adicionado");
      onSaved();
    } catch (e) { toast.error("Erro"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Adicionar participante manual</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nome *</Label><Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="ap-nome" /></div>
          <div>
            <Label>Função</Label>
            <Select value={form.funcao} onValueChange={(v) => setForm({ ...form, funcao: v })}>
              <SelectTrigger data-testid="ap-funcao"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Vereador", "Presidente", "Assessor", "Secretário", "Contador", "Servidor", "Outro"].map((f) => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label>Município</Label><Input value={form.municipio_nome} onChange={(e) => setForm({ ...form, municipio_nome: e.target.value })} data-testid="ap-mun" /></div>
            <div><Label>UF</Label><Input maxLength={2} value={form.uf} onChange={(e) => setForm({ ...form, uf: e.target.value.toUpperCase() })} data-testid="ap-uf" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={salvar} data-testid="btn-add-p-salvar">Adicionar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Modal: Novo Curso (criar do zero direto pelo módulo Gestão de Cursos)
// =============================================================================
function NovoCursoModal({ open, onClose, nucleos, onSaved }) {
  const hoje = new Date();
  const mesAtual = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
  const [form, setForm] = useState({
    nome: "",
    nucleo_id: "",
    mes_ano: mesAtual,
    data_realizacao: "",
    valor_cheio: 0,
    meta_inscritos: 0,
    meta_receita: 0,
    status: "ativo",
    professor: "",
    data_inicio: "",
    data_fim: "",
    carga_horaria_total: "",
    local_realizacao: "",
    uf: "",
    emissor_id: "",
    diretor_nucleo_id: "",
  });
  const [emissores, setEmissores] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/emissores").then((r) => setEmissores(r.data || [])).catch(() => {});
    api.get("/users").then((r) => setUsers((r.data || []).filter((u) => ["diretoria", "supervisao"].includes(u.role)))).catch(() => {});
    setForm({
      nome: "",
      nucleo_id: "",
      mes_ano: mesAtual,
      data_realizacao: "",
      valor_cheio: 0,
      meta_inscritos: 0,
      meta_receita: 0,
      status: "ativo",
      professor: "",
      data_inicio: "",
      data_fim: "",
      carga_horaria_total: "",
      local_realizacao: "",
      uf: "",
      emissor_id: "",
      diretor_nucleo_id: "",
    });
  }, [open]);  // eslint-disable-line

  const salvar = async () => {
    if (!form.nome.trim()) { toast.error("Nome é obrigatório"); return; }
    if (!form.nucleo_id) { toast.error("Selecione o núcleo"); return; }
    if (!form.data_inicio || !form.data_fim) { toast.error("Informe a data de início e a de término"); return; }
    if (!form.uf) { toast.error("Selecione a UF de realização (ou Online)"); return; }
    try {
      await api.post("/cursos", {
        ...form,
        valor_cheio: Number(form.valor_cheio) || 0,
        meta_inscritos: Number(form.meta_inscritos) || 0,
        meta_receita: Number(form.meta_receita) || 0,
      });
      toast.success("Curso criado");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao criar curso");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Novo Curso</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nome do curso *</Label>
            <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex: Nova Lei de Licitações" data-testid="nc-nome" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Núcleo *</Label>
              <Select value={form.nucleo_id} onValueChange={(v) => setForm({ ...form, nucleo_id: v })}>
                <SelectTrigger data-testid="nc-nucleo"><SelectValue placeholder="Selecionar…" /></SelectTrigger>
                <SelectContent>
                  {nucleos.map((n) => (<SelectItem key={n.id} value={n.id}>{n.nome}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>UF de realização *</Label>
              <UfCursoSelect value={form.uf} onChange={(v) => setForm({ ...form, uf: v })} testid="nc-uf" />
            </div>
          </div>
          <div>
            <Label>Professor / Instrutor *</Label>
            <Input value={form.professor} onChange={(e) => setForm({ ...form, professor: e.target.value })} placeholder="Ex: Prof. Dr. Silvio Almeida" data-testid="nc-professor" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Data de início *</Label>
              <Input type="date" value={form.data_inicio} onChange={(e) => setForm({ ...form, data_inicio: e.target.value })} data-testid="nc-inicio" />
            </div>
            <div>
              <Label>Data de término *</Label>
              <Input type="date" value={form.data_fim} onChange={(e) => setForm({ ...form, data_fim: e.target.value })} data-testid="nc-fim" />
            </div>
          </div>
          <p className="text-[11px] text-slate-500 -mt-1">O mês do curso é definido pela data de início.</p>
          <div>
            <Label>Dias no texto (para proposta/confirmação)</Label>
            <Input value={form.data_realizacao} onChange={(e) => setForm({ ...form, data_realizacao: e.target.value })} placeholder='Ex: "20 a 22"' data-testid="nc-dias" />
          </div>
          <div>
            <Label>Carga horária *</Label>
            <Input value={form.carga_horaria_total} onChange={(e) => setForm({ ...form, carga_horaria_total: e.target.value })} placeholder="Ex: 32 horas" data-testid="nc-carga" />
          </div>
          <div>
            <Label>Cidade de realização {form.uf === "Online" ? "(opcional)" : "*"}</Label>
            <Input value={form.local_realizacao} onChange={(e) => setForm({ ...form, local_realizacao: e.target.value })} placeholder="Ex: Hotel Nacional, Brasília/DF" data-testid="nc-local" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>PJ Emissora (fatura) *</Label>
              <Select value={form.emissor_id} onValueChange={(v) => setForm({ ...form, emissor_id: v })}>
                <SelectTrigger data-testid="nc-emissor"><SelectValue placeholder="Selecionar…" /></SelectTrigger>
                <SelectContent>
                  {emissores.map((e) => (<SelectItem key={e.id} value={e.id}>{e.razao_social}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Diretor de Núcleo *</Label>
              <Select value={form.diretor_nucleo_id} onValueChange={(v) => setForm({ ...form, diretor_nucleo_id: v })}>
                <SelectTrigger data-testid="nc-diretor"><SelectValue placeholder="Selecionar…" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Valor cheio unitário *</Label>
              <Input type="number" step="0.01" value={form.valor_cheio} onChange={(e) => setForm({ ...form, valor_cheio: e.target.value })} data-testid="nc-valor" />
            </div>
            <div>
              <Label>Meta inscritos *</Label>
              <Input type="number" value={form.meta_inscritos} onChange={(e) => setForm({ ...form, meta_inscritos: e.target.value })} data-testid="nc-meta-i" />
            </div>
            <div>
              <Label>Meta receita *</Label>
              <Input type="number" step="0.01" value={form.meta_receita} onChange={(e) => setForm({ ...form, meta_receita: e.target.value })} data-testid="nc-meta-r" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={salvar} className="bg-[#1d7495] hover:bg-[#0f5673]" data-testid="btn-salvar-novo-curso">Criar curso</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

